/**
 * signup-id/index.tsx  — Auth0 ACUL screen: "signup-id"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Step 1 of the 3-step signup flow: "Your details".
 * Collects first name, last name, email, and mobile number.
 * Maps to the `signup-id` ACUL screen; calls `signupId.signup({ email, phone })`.
 *
 * After this screen Auth0 may route to:
 *   - `login-passwordless-sms-otp` (if post-signup MFA is enforced)
 *   - `signup-password` (directly, if phone MFA is not required yet)
 *
 * MULTI-STEP SIGNUP NOTE (Auth0 Limitation)
 * -----------------------------------------
 * The mockup shows a 3-step flow: details → verify phone → create password.
 * Auth0 ACUL does NOT natively support a custom phone-OTP step between
 * signup-id and signup-password inside Universal Login.
 *
 * "Step 2: Verify phone" maps to:
 *   a) Auth0 MFA enrollment (mfa-sms-enrollment / mfa-sms-challenge screens)
 *      triggered on first login if MFA is required; OR
 *   b) A Post-User-Registration Auth0 Action that sends an SMS and requires
 *      verification before allowing the user into the app.
 *   TODO: configure Auth0 MFA policy or a Post-Registration Action.
 *
 * The stepper renders all 3 steps visually (step 1 active), but steps 2 and 3
 * are driven by subsequent Auth0 screens, NOT by React state in this file.
 *
 * CONSENT FIELDS
 * --------------
 * The SMS marketing and terms-of-service checkboxes are shown below the form.
 * These values are NOT sent to Auth0 by default — store them via an Auth0 Action
 * that captures app_metadata.  TODO: implement consent capture Action.
 *
 * HOW TO EXTEND
 * -------------
 * To collect additional fields (username, address, etc.), add <CustomInput>
 * elements and extend the SignupOptions payload passed to handleSignup.
 */

import { useId, useState } from "react";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { CustomInput } from "@/components/auth/CustomInput";
import { SignupStepper } from "@/components/auth/SignupStepper";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useErrors } from "@auth0/auth0-acul-react/signup-id";
import { useSignupIdManager } from "./hooks/useSignupIdManager";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
}

function isValidPhone(v: string) {
  const stripped = v.replace(/\D/g, "");
  return (
    stripped.length === 10 || (stripped.length === 11 && stripped[0] === "1")
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function SignupIdScreen() {
  const { signupId, texts, locales, loginLink, handleSignup } =
    useSignupIdManager();

  const uid = useId();
  document.title =
    texts?.pageTitle || locales?.page?.title || "Create your account";
  applyAuth0Theme(signupId);

  const { errors } = useErrors();
  const firstError = errors.byType("auth0")[0]?.message ?? undefined;

  // Local form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consentSms, setConsentSms] = useState(true);
  const [consentTerms, setConsentTerms] = useState(true);
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const emailError =
    touched && email && !isValidEmail(email)
      ? "Enter a valid email address."
      : undefined;
  const phoneError =
    touched && phone && !isValidPhone(phone)
      ? "Enter a valid 10-digit US or Canada phone number."
      : undefined;

  const canSubmit =
    !isLoading &&
    firstName.trim() &&
    lastName.trim() &&
    isValidEmail(email) &&
    isValidPhone(phone) &&
    consentTerms &&
    !emailError &&
    !phoneError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setIsLoading(true);
    await handleSignup({
      email: email.trim(),
      phone: phone.trim(),
      // NOTE: firstName/lastName must be captured via an Auth0 Action that
      // reads user_metadata from the sign-up request.
      // TODO: pass first_name / last_name via additional_signup_fields or
      //       an Action that sets user_metadata.
    });
    setIsLoading(false);
  };

  return (
    <AuthLayout
      title="Become a member"
      signupVariant
      headerSlot={<SignupStepper activeStep={1} />}
    >
      <form
        id={`${uid}-form`}
        className="auth-view"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Auth0 server error */}
        {firstError && (
          <div className="auth-error-banner" role="alert">
            {firstError}
          </div>
        )}

        {/* Row: first + last name */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--portal-space-4)",
          }}
        >
          <CustomInput
            id={`${uid}-first`}
            label="First name"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <CustomInput
            id={`${uid}-last`}
            label="Last name"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>

        <CustomInput
          id={`${uid}-email`}
          label="Email address"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          error={emailError}
          required
        />

        <CustomInput
          id={`${uid}-phone`}
          label="Mobile number"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(555) 555-5555"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={() => setTouched(true)}
          error={phoneError}
          hint="U.S. and Canada numbers only (country code +1)."
          required
        />

        {/* Consent checkboxes */}
        <div className="signup-consent-group">
          <label className="signup-consent">
            <input
              type="checkbox"
              className="signup-check"
              checked={consentSms}
              onChange={(e) => setConsentSms(e.target.checked)}
            />
            <span className="signup-consent-box" aria-hidden="true">
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8 L7 12 L13 4"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="signup-consent-text">
              <span>
                By checking this box you consent to receive marketing SMS/text
                messages from Veteran Strong. Your consent is not required to do
                business with us.
              </span>
              <span className="signup-consent-optional">
                Optional — you can create your account without checking this
                box.
              </span>
            </span>
          </label>

          <label className="signup-consent">
            <input
              type="checkbox"
              className="signup-check"
              checked={consentTerms}
              onChange={(e) => setConsentTerms(e.target.checked)}
              required
            />
            <span className="signup-consent-box" aria-hidden="true">
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8 L7 12 L13 4"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="signup-consent-text">
              <span>
                By checking this box and clicking &ldquo;Next&rdquo;, you agree
                to our{" "}
                <a href="/privacy-policy" className="signup-consent-link">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/terms-of-use" className="signup-consent-link">
                  Terms of Use
                </a>
                .
              </span>
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="auth-submit-block">
          <button type="submit" className="auth-btn" disabled={!canSubmit}>
            {isLoading ? "Please wait…" : "Next"}
          </button>

          {loginLink && (
            <p className="auth-nav-link">
              Already a member? <a href={loginLink}>Log in here</a>
            </p>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}

export default SignupIdScreen;
